import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const CHUNK = 400; // client-side safety net; backend caps at 500 per call

export function useSymbols(trades) {
  const symbolsKey = useMemo(() => {
    const map = new Map();
    trades.forEach((t) => {
      const key = t.stock?.toUpperCase();
      if (key && !map.has(key)) map.set(key, { symbol: key, exchange: t.exchange || "NS" });
    });
    return JSON.stringify(Array.from(map.values()));
  }, [trades]);
  return useMemo(() => JSON.parse(symbolsKey), [symbolsKey]);
}

export function usePrices(symbols) {
  const [prices, setPrices] = useState({});
  const [fetching, setFetching] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchPrices = useCallback(async (force = false) => {
    if (symbols.length === 0) return;
    setFetching(true);
    try {
      const endpoint = force ? "/prices/refresh" : "/prices/batch";
      const merged = {};
      for (let i = 0; i < symbols.length; i += CHUNK) {
        const { data } = await api.post(endpoint, { symbols: symbols.slice(i, i + CHUNK) });
        Object.assign(merged, data);
      }
      setPrices(merged);
      setLastFetch(new Date());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to refresh LTPs");
    } finally {
      setFetching(false);
    }
  }, [symbols]);

  // Auto-load (from daily cache) whenever the tracked symbol set changes
  useEffect(() => { fetchPrices(false); }, [fetchPrices]);

  const cacheDate = useMemo(
    () => Object.values(prices).find((v) => v?.cache_date)?.cache_date || null,
    [prices],
  );

  return { prices, fetching, lastFetch, cacheDate, fetchPrices };
}
